import { IconAlertTriangle } from '@tabler/icons-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { useTranslation } from '../../i18n/hooks/useTranslation';

// variant: 'danger' (red, default) | 'primary' (orange)
export default function ConfirmDialog({
    open,
    onClose,
    onConfirm,
    title,
    description,
    confirmLabel,
    cancelLabel,
    loading = false,
    variant = 'danger',
    children,
    // z-index classes — overridable so the confirmation can sit ABOVE a very high
    // stacking-context modal (e.g. the fullscreen ImagePreviewModal at z-[99999]).
    zClass = 'z-[200]',
    overlayZClass = 'z-[199]',
}) {
    const { t } = useTranslation();

    const iconBg   = variant === 'primary' ? 'bg-orange-50 dark:bg-orange-900/20'  : 'bg-red-50 dark:bg-red-900/20';
    const iconColor = variant === 'primary' ? 'text-orange-500'                     : 'text-red-500';
    const btnColor  = variant === 'primary'
        ? 'text-orange-500 hover:bg-orange-50 dark:hover:bg-orange-900/20'
        : 'text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20';

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className={`max-w-sm p-0 overflow-hidden ${zClass}`} overlayClassName={overlayZClass} aria-describedby={undefined}>
                <DialogTitle className="sr-only">{title}</DialogTitle>
                <div className="p-5 flex flex-col items-center text-center gap-3">
                    <div className={`w-12 h-12 rounded-full ${iconBg} flex items-center justify-center`}>
                        <IconAlertTriangle size={24} className={iconColor} />
                    </div>
                    <div>
                        <p className="text-sm font-semibold text-zinc-800 dark:text-zinc-100">{title}</p>
                        {description && <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">{description}</p>}
                    </div>
                    {children}
                </div>
                <div className="flex border-t border-zinc-100 dark:border-zinc-800">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={loading}
                        className="flex-1 py-3 text-sm font-medium text-zinc-600 dark:text-zinc-400 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors border-r border-zinc-100 dark:border-zinc-800 disabled:opacity-50"
                    >
                        {cancelLabel || t('common.cancel')}
                    </button>
                    <button
                        type="button"
                        onClick={onConfirm}
                        disabled={loading}
                        className={`flex-1 py-3 text-sm font-semibold transition-colors disabled:opacity-50 ${btnColor}`}
                    >
                        {loading ? '...' : (confirmLabel || t('common.confirm'))}
                    </button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
